import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { throttle } from 'lodash-es';
import { type CustomerPairingRatingDetail } from '@hungryroot/rest';
import { type DeleteCustomerPairingRatingDetailParams } from '@hungryroot/rest/api/v2/customers/[id]/pairing_ratings/[pairing_id]/delete';
import { useIdentify } from '@hungryroot/rest-hooks';
import { useDeleteCustomerPairingRating } from '../../../hooks/useDeleteCustomerPairingRating';
import {
  type PairingPayload,
  usePatchCustomerPairingRatings,
} from '../../../hooks/usePatchCustomerPairingRatings';
import { usePostCustomerPairingRatings } from '../../../hooks/usePostCustomerPairingRatings';
import { invalidateQuery as invalidateSelfFoodFeedbacksQuery } from '../../../hooks/useSelfFoodFeedbacks';
import {
  useSelfPairingRating,
  invalidateQuery as invalidateSelfPairingRatingQuery,
  getQueryKey as getSelfPairingRatingQueryKey,
} from '../../../hooks/useSelfPairingRating';
import { useOptimisticUpdateStatus } from '../useOptimisticUpdateStatus';
import { type RatablePairingItem } from '../useRatableItems';

type UseThumbsPairingRatingProps = {
  item: RatablePairingItem;
  onError?: () => void;
  enableInitialFetch?: boolean;
  initialRating?: boolean | null;
};

type MutationContext = {
  previousRating?: CustomerPairingRatingDetail;
};

export function useThumbsPairingRating(props: UseThumbsPairingRatingProps) {
  const { item, onError, enableInitialFetch = false, initialRating } = props;

  const {
    optimisticUpdateStatus,
    postCustomerPairingRatingsMutate,
    deleteCustomerPairingRatingsMutate,
    patchCustomerPairingRatingsMutate,
    hasRated,
  } = useRatingMutation({
    item,
    onError,
    enableInitialFetch,
  });

  const { customerData } = useIdentify();
  const [hasSetInitialRating, setHasSetInitialRating] = useState(false);

  const {
    data: selfPairingRatingData,
    isPending: selfPairingRatingIsPending,
    error: selfPairingRatingError,
  } = useSelfPairingRating({
    params: {
      pairing_id: item.pairingId,
    },
    options: {
      enabled: hasRated,
    },
  });

  // Throttled API calls to prevent sending too many requests if the user
  // decides to rage click. This function handles which API call to make
  // based on the current state of the rating.
  const memoizedOnRatingApiCall = useMemo(
    () =>
      throttle((newValue: boolean | null) => {
        if (!customerData?.id) {
          return;
        }

        // There are no previous rating that we know of. Create a new one only
        // if the rating is a thumbs up or thumbs down (i.e., not `null`).
        if (!selfPairingRatingData && newValue !== null) {
          postCustomerPairingRatingsMutate({
            customerId: customerData?.id,
            payload: {
              order_id: item.orderNumber,
              pairing: item.pairingId,
              liked: newValue,
            },
          });

          return;
        }

        // There's a previous rating and the user opted to remove it.
        if (selfPairingRatingData && newValue === null) {
          deleteCustomerPairingRatingsMutate({
            customer_id: String(customerData.id),
            pairing_id: item.pairingId,
          });

          return;
        }

        // There's a previous rating and the user opted to change it (not deactivate).
        if (selfPairingRatingData && newValue !== null) {
          patchCustomerPairingRatingsMutate({
            customerId: customerData.id,
            payload: {
              order_id: item.orderNumber,
              pairing: item.pairingId,
              liked: newValue,
            },
          });

          return;
        }
      }, 1000),
    [
      selfPairingRatingData,
      customerData?.id,
      postCustomerPairingRatingsMutate,
      deleteCustomerPairingRatingsMutate,
      patchCustomerPairingRatingsMutate,
      item.orderNumber,
      item.pairingId,
    ]
  );

  // If given an intitial rating, handle the API call to set it if it does not match
  // what the server gives us.
  useEffect(() => {
    if (
      selfPairingRatingIsPending ||
      initialRating === undefined ||
      hasSetInitialRating
    ) {
      return;
    }

    if (
      (selfPairingRatingError && initialRating !== null) ||
      (selfPairingRatingData && selfPairingRatingData.liked !== initialRating)
    ) {
      memoizedOnRatingApiCall(initialRating);
      setHasSetInitialRating(true);
    }
  }, [
    selfPairingRatingData,
    initialRating,
    hasSetInitialRating,
    memoizedOnRatingApiCall,
    selfPairingRatingData?.liked,
    selfPairingRatingIsPending,
    selfPairingRatingError,
  ]);

  let rating;

  if (initialRating && !hasSetInitialRating) {
    rating = initialRating;
  } else if (
    initialRating &&
    hasSetInitialRating &&
    selfPairingRatingIsPending
  ) {
    rating = initialRating;
  } else {
    rating = selfPairingRatingData?.liked ?? null;
  }

  return {
    rating,
    onRating: memoizedOnRatingApiCall,
    optimisticUpdateStatus,
  };
}

type UseRatingMutationProps = {
  item: RatablePairingItem;
  onError?: () => void;
  enableInitialFetch: boolean;
};

function useRatingMutation(props: UseRatingMutationProps) {
  const { item, onError, enableInitialFetch } = props;
  const queryKey = getSelfPairingRatingQueryKey({ pairing_id: item.pairingId });

  // Keeps track of whether or not the user has rated this pairing at least
  // once. This is used to determine whether or not we should initally fetch
  // the latest rating from the server.
  const [hasRated, setHasRated] = useState(enableInitialFetch);

  const { optimisticUpdateStatus, setOptimisticUpdateStatus } =
    useOptimisticUpdateStatus();

  const queryClient = useQueryClient();

  const onPostOrPatchMutate = async ({
    payload,
  }: {
    payload: PairingPayload;
  }) => {
    setOptimisticUpdateStatus('pending');

    await queryClient.cancelQueries({ queryKey });

    const previousRating: CustomerPairingRatingDetail | undefined =
      queryClient.getQueryData(queryKey);

    // Create an object that represents the pairing rating details as
    // if the API call was successful.
    const optimisticUpdate: CustomerPairingRatingDetail = {
      ...previousRating,
      pairing: item.pairingId,
      order: item.orderNumber,
      liked: payload.liked,
    };

    queryClient.setQueryData(queryKey, optimisticUpdate);

    // Provide context for the mutation so we can revert the optimistic
    // update if anything goes wrong.
    return { previousRating };
  };

  // `onError` handler when making the `POST` or `PATCH` call.
  const onPostOrPatchError = () => {
    onError?.();

    // Remove the query data from the cache. If something went wrong here,
    // the data in the cache is likely not accurate and may cause us to attempt
    // to make the wrong type of request. Best to start off fresh.
    queryClient.removeQueries({ queryKey });
  };

  // `onSettled` handler when making the `POST`, `PATCH`, or `DELETE` call.
  const onSettled = () => {
    setOptimisticUpdateStatus('settled');
    setHasRated(true);

    // Invalidate the query so that the latest rating is fetched from the
    // server. The timeout is to prevent the GET from happening too soon
    // after to prevent stale data.
    setTimeout(() => {
      invalidateSelfPairingRatingQuery({
        pairing_id: item.pairingId,
        queryClient,
      });

      invalidateSelfFoodFeedbacksQuery({
        params: {
          order: item.orderNumber,
          bundle:
            'bundleId' in item && item.bundleId ? item.bundleId : undefined,
          pairing:
            'pairingId' in item && item.pairingId ? item.pairingId : undefined,
        },
        queryClient,
      });
    }, 500);
  };

  const { mutate: postCustomerPairingRatingsMutate } =
    usePostCustomerPairingRatings<MutationContext>({
      onMutate: onPostOrPatchMutate,
      onError: onPostOrPatchError,
      onSettled,
    });

  const { mutate: patchCustomerPairingRatingsMutate } =
    usePatchCustomerPairingRatings<MutationContext>({
      onMutate: onPostOrPatchMutate,
      onError: onPostOrPatchError,
      onSettled,
      // Remove the `onSuccess` handler from `usePatchCustomerPairingRatings()`
      // since we're using the `onSettled` handler to handle things.
      onSuccess: undefined,
    });

  const { mutate: deleteCustomerPairingRatingsMutate } =
    useDeleteCustomerPairingRating({
      onMutate: async () => {
        setOptimisticUpdateStatus('pending');

        await queryClient.cancelQueries({ queryKey });

        const previousRating: CustomerPairingRatingDetail | undefined =
          queryClient.getQueryData(queryKey);

        queryClient.setQueryData(queryKey, null);

        return { previousRating };
      },

      onError: (
        _error: Error,
        _variables: DeleteCustomerPairingRatingDetailParams,
        context?: MutationContext
      ) => {
        onError?.();

        if (context) {
          queryClient.setQueryData(queryKey, context.previousRating);
        }
      },

      onSettled,

      // Remove the `onSuccess` handler from `useDeleteCustomerPairingRating()`
      // since we're using the `onSettled` handler to handle things.
      onSuccess: undefined,
    });

  return {
    optimisticUpdateStatus,
    postCustomerPairingRatingsMutate,
    deleteCustomerPairingRatingsMutate,
    patchCustomerPairingRatingsMutate,
    hasRated,
  };
}
